import {
  GetTokenSilentlyOptions,
  LogoutOptions,
  RedirectLoginOptions,
  User,
} from "@auth0/auth0-react";
import { captureSentryException } from "src/utils/sentry";

let getAccessTokenSilently: getAccessTokenSilentlyType = () =>
  Promise.resolve("");

// Store other Auth0 functions
let loginWithRedirect:
  | ((options?: RedirectLoginOptions) => Promise<void>)
  | null = null;
let logout: ((options?: LogoutOptions) => void) | null = null;
let user: User | undefined = undefined;
let isAuthenticated: boolean = false;

function parseJwt(token: string) {
  var base64Url = token.split(".")[1];
  var base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
  var jsonPayload = decodeURIComponent(
    window
      .atob(base64)
      .split("")
      .map(function (c) {
        return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
      })
      .join("")
  );

  return JSON.parse(jsonPayload);
}

export const authHelper = {
  // Set Auth0 functions from useAuth0 hook
  setAuth0Functions: (functions: {
    loginWithRedirect: (options?: RedirectLoginOptions) => Promise<void>;
    logout: (options?: LogoutOptions) => void;
    user: User | undefined;
    isAuthenticated: boolean;
    getAccessTokenSilently: getAccessTokenSilentlyType;
  }) => {
    loginWithRedirect = functions.loginWithRedirect;
    logout = functions.logout;
    user = functions.user;
    isAuthenticated = functions.isAuthenticated;
    getAccessTokenSilently = functions.getAccessTokenSilently;
  },

  // Get Auth0 functions
  getAuth0Functions: () => ({
    loginWithRedirect,
    logout,
    user,
    isAuthenticated,
    getAccessTokenSilently,
  }),

  // Helper methods for easier access
  getUserId: async (): Promise<string> => {
    // parse the token and get the user id
    const token = await getAccessTokenSilently();
    const decoded = parseJwt(token);
    const id = decoded["https://formly.ai/uuid"];
    if (!id) {
      throw new Error("User ID not found");
    }
    return id;
  },
  getUser: () => user,
  isUserAuthenticated: () => isAuthenticated,
  loginUser: async (options?: RedirectLoginOptions) => {
    if (loginWithRedirect) {
      return await loginWithRedirect(options);
    }
    throw new Error("Auth0 login function not available");
  },
  logoutUser: (options?: LogoutOptions) => {
    if (logout) {
      return logout(options);
    }
    throw new Error("Auth0 logout function not available");
  },

  // Handle authentication errors with automatic login redirect
  handleAuthError: async (error: any) => {
    // Check if this is a token-related error
    if (error?.message?.includes("401") || error?.message?.includes("403")) {
      try {
        // Try to refresh the token once more
        await authHelper.getAuth0Functions().getAccessTokenSilently();
      } catch (refreshError) {
        captureSentryException(refreshError);

        // Directly use loginWithRedirect if available
        if (loginWithRedirect) {
          await loginWithRedirect({
            appState: {
              returnTo: window.location.pathname + window.location.search,
            },
          });
        } else {
          captureSentryException(
            new Error(
              "Auth error: redirecting to login but loginWithRedirect is not available"
            )
          );
        }
      }
    } else {
      // For non-auth errors, just log to Sentry
      captureSentryException(error);
    }
  },
};

type getAccessTokenSilentlyType = (
  options?: GetTokenSilentlyOptions
) => Promise<string>;
